/**
 * Hub Analytics — sections repliables (critiques I/E, SPOF, readiness).
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  analyticsApi,
  ApiRequestError,
  type CampaignInsightDto,
  type CriticalWorkforceDto,
  type SpofCriticalDto,
} from "../../api/api";
import {
  AnalyticsFilterBar,
  lineTitle,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

function Section({
  title,
  summary,
  open,
  onToggle,
  children,
}: {
  title: string;
  summary?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="sm-card sm-assess-block" style={{ marginBottom: 12 }}>
      <button type="button" className="sm-assess-macro" onClick={onToggle}>
        <span>
          {title}
          {summary ? (
            <span className="sm-muted" style={{ marginLeft: 10, fontWeight: 400, fontSize: 13 }}>
              {summary}
            </span>
          ) : null}
        </span>
        <span aria-hidden style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}>
          ▾
        </span>
      </button>
      {open ? <div className="sm-card-pad">{children}</div> : null}
    </div>
  );
}

export function AnalyticsHubPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [open, setOpen] = useState({ critical: true, spof: false, campaign: false, links: false });
  const [critical, setCritical] = useState<CriticalWorkforceDto | null>(null);
  const [spof, setSpof] = useState<SpofCriticalDto | null>(null);
  const [campaign, setCampaign] = useState<CampaignInsightDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([
      analyticsApi.criticalWorkforce(filters),
      analyticsApi.spofCritical(filters),
      analyticsApi.campaign(filters),
    ])
      .then(([c, s, camp]) => {
        if (!cancelled) {
          setCritical(c);
          setSpof(s);
          setCampaign(camp);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  const toggle = (key: keyof typeof open) =>
    setOpen((o) => ({ ...o, [key]: !o[key] }));

  return (
    <div className="sm-page">
      <PageHeader title={t("analytics_hub")} subtitle={t("analytics_hub_sub")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? <p className="sm-muted">{t("loading")}</p> : null}

      <Section
        title={t("insight_critical_ie")}
        summary={
          critical
            ? `${critical.summary.onlyExternal} ${t("only_external")} · ${critical.summary.uncovered} ${t("uncovered")}`
            : undefined
        }
        open={open.critical}
        onToggle={() => toggle("critical")}
      >
        <p className="sm-muted" style={{ marginTop: 0 }}>
          {t("target_threshold")}: <strong>{critical?.target}</strong>
        </p>
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>Line</th>
                <th>{t("workforce_internal")}</th>
                <th>{t("workforce_external")}</th>
                <th>Flag</th>
              </tr>
            </thead>
            <tbody>
              {(critical?.lines ?? []).slice(0, 40).map((row) => {
                const { title, subtitle } = lineTitle(row.line);
                return (
                  <tr key={row.line.id}>
                    <td>
                      <div>{title}</div>
                      <div className="sm-muted" style={{ fontSize: 11 }}>
                        {subtitle}
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {row.internalAbove}/{row.internalTotal}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {row.externalAbove}/{row.externalTotal}
                    </td>
                    <td>
                      {row.uncovered ? (
                        <span className="sm-badge-pill sm-badge-red">{t("uncovered")}</span>
                      ) : row.onlyExternal ? (
                        <span className="sm-badge-pill sm-badge-yellow">{t("only_external")}</span>
                      ) : (
                        <span className="sm-badge-pill sm-badge-green">OK</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {critical && critical.lines.length === 0 ? (
                <tr>
                  <td colSpan={4} className="sm-muted" style={{ textAlign: "center" }}>
                    {t("no_data")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <p className="sm-muted" style={{ marginTop: 8 }}>
          <Link to="/analytics/coverage">{t("team_coverage")}</Link>
        </p>
      </Section>

      <Section
        title={t("insight_spof")}
        summary={
          spof
            ? `${spof.summary.uncovered} ${t("uncovered")} · ${spof.summary.spof} SPOF`
            : undefined
        }
        open={open.spof}
        onToggle={() => toggle("spof")}
      >
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>Line</th>
                <th>≥ seuil</th>
                <th>Risk</th>
              </tr>
            </thead>
            <tbody>
              {(spof?.items ?? []).map((item) => {
                const { title, subtitle } = lineTitle(item.line);
                return (
                  <tr key={item.line.id}>
                    <td>
                      <div>{title}</div>
                      <div className="sm-muted" style={{ fontSize: 11 }}>
                        {subtitle}
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {item.aboveTarget}/{item.total}
                    </td>
                    <td>
                      <span
                        className={`sm-badge-pill ${
                          item.risk === "uncovered" ? "sm-badge-red" : "sm-badge-yellow"
                        }`}
                      >
                        {item.risk === "uncovered" ? t("uncovered") : "SPOF"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {spof && spof.items.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ textAlign: "center", color: "#059669" }}>
                    {t("spof_none")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title={t("insight_campaign")}
        summary={
          campaign
            ? `${campaign.totals.validated}/${campaign.totals.total} ${t("status_validated")}`
            : undefined
        }
        open={open.campaign}
        onToggle={() => toggle("campaign")}
      >
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("domain")}</th>
                <th>{t("status_draft")}</th>
                <th>{t("status_submitted")}</th>
                <th>{t("status_validated")}</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {(campaign?.domains ?? []).map((d) => (
                <tr key={d.domain}>
                  <td>{d.domain}</td>
                  <td style={{ textAlign: "center" }}>{d.draft}</td>
                  <td style={{ textAlign: "center" }}>{d.submitted}</td>
                  <td style={{ textAlign: "center" }}>{d.validated}</td>
                  <td style={{ textAlign: "center" }}>{d.validatedPct}%</td>
                </tr>
              ))}
              {campaign && campaign.domains.length === 0 ? (
                <tr>
                  <td colSpan={5} className="sm-muted" style={{ textAlign: "center" }}>
                    {t("no_data")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <p className="sm-muted" style={{ marginTop: 8 }}>
          <Link to="/validation">{t("validation_dashboard")}</Link>
        </p>
      </Section>

      <Section
        title={t("insight_more_views")}
        open={open.links}
        onToggle={() => toggle("links")}
      >
        <ul className="sm-insight-links">
          <li>
            <Link to="/analytics/domain-bench">{t("domain_bench")}</Link>
          </li>
          <li>
            <Link to="/analytics/plaque">{t("plaque_view")}</Link>
          </li>
          <li>
            <Link to="/analytics/rare-expertise">{t("rare_expertise")}</Link>
          </li>
          <li>
            <Link to="/analytics/heatmap">{t("heat_map")}</Link>
          </li>
          <li>
            <Link to="/analytics/radar">{t("radar_chart")}</Link>
          </li>
          <li>
            <Link to="/analytics/gaps">{t("gap_analysis")}</Link>
          </li>
          <li>
            <Link to="/analytics/training-needs">{t("training_needs")}</Link>
          </li>
          <li>
            <Link to="/analytics/transversal-team">{t("transversal_team")}</Link>
          </li>
        </ul>
        <p className="sm-muted">{t("analytics_catalog_hint")}</p>
      </Section>
    </div>
  );
}
